import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import SEO from '../components/common/SEO';
import { RiArrowLeftLine, RiDownload2Line, RiExternalLinkLine } from 'react-icons/ri';

const TOTAL_PAGES = 33;
const REPORT_IMAGES = Array.from({ length: TOTAL_PAGES }, (_, i) => ({
  id: i + 1,
  src: `/images/architectures/ecommerce/report/images/page_${i + 1}.png`,
  title: `Page ${i + 1}`
}));

function EcommerceReport() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main style={{ paddingTop: '80px', minHeight: '100vh', background: '#0f172a', color: 'white', fontFamily: '"General Sans", sans-serif' }}>
      <SEO 
        title="E-Commerce Architecture Report | Eagentix AI Cloud Architect" 
        description="Full 33-page Architecture Design Document for E-Commerce Platform Migration generated by Eagentix AI Cloud Architect."
      />

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Back Link */}
        <Link to="/agents/cloud-architect#ecommerce" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#94a3b8', textDecoration: 'none', marginBottom: '32px', fontSize: '0.9rem', fontWeight: 600, transition: 'color 0.2s' }} onMouseEnter={(e) => e.target.style.color = '#38bdf8'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
          <RiArrowLeftLine /> Back to Cloud Architect
        </Link>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '24px', marginBottom: '48px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '32px' }}>
          <div style={{ flex: 1, minWidth: '320px' }}>
            <span style={{ color: '#38bdf8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>E-Commerce Platform Migration</span>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: 'white', margin: '12px 0 16px', letterSpacing: '-0.02em' }}>Architecture Design Document</h1>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', lineHeight: 1.6, maxWidth: '700px', margin: 0 }}>
              This 33-page document was autonomously generated by the Eagentix AI Cloud Architect Agent, covering requirements traceability, multi-region topology, security architecture, and a 16-week phased migration plan.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <a 
              href="/images/architectures/ecommerce/report/payment_Architecture_Architecture_Design_Document.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontWeight: 700, textDecoration: 'none', transition: 'all 0.2s' }}
            >
              <RiDownload2Line /> Download PDF
            </a>
          </div>
        </div>

        {/* Gallery Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '32px' }}>
          {REPORT_IMAGES.map((img) => (
            <motion.div
              key={img.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: (img.id % 5) * 0.05 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <div style={{ position: 'relative', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', background: 'white', cursor: 'zoom-in', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }} onClick={() => window.open(img.src, '_blank')}>
                <img src={img.src} alt={img.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', transition: 'background 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={(e) => e.target.style.background = 'rgba(0,0,0,0.1)'} onMouseLeave={(e) => e.target.style.background = 'rgba(0,0,0,0)'}>
                   <RiExternalLinkLine size={32} color="white" style={{ opacity: 0, transition: 'opacity 0.3s' }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b' }}>Page {img.id.toString().padStart(2, '0')}</span>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Eagentix AI Architect</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Footer */}
        <div style={{ marginTop: '80px', padding: '60px 40px', borderRadius: '32px', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', border: '1px solid rgba(56,189,248,0.2)', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '20px' }}>Ready to generate your own architecture?</h2>
          <p style={{ fontSize: '1.1rem', color: '#94a3b8', marginBottom: '32px', maxWidth: '600px', margin: '0 auto 32px' }}>
            Get access to the Cloud Architect Agent and start designing enterprise-grade infrastructure in minutes.
          </p>
          <a href="https://app.eagentix.com/register?reason=Requesting%20access%20to%20Cloud%20Architect%20Agent" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '16px 32px', borderRadius: '12px', background: '#38bdf8', color: '#0f172a', fontWeight: 800, fontSize: '1.05rem', textDecoration: 'none', boxShadow: '0 4px 14px rgba(56,189,248,0.4)' }}>
            Start Building with the Agent
          </a>
        </div>
      </div>
    </main>
  );
}

export default EcommerceReport;
